/**
 * Simply Amazing - Speed & Core Web Vitals Optimization Engine
 * Accelerates page load time to < 3 seconds on both Desktop & Mobile.
 */
(function() {
  'use strict';

  // Check for data-saver mode or slow 2G connection
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const isDataSaver = connection && (connection.saveData || /2g/.test(connection.effectiveType));

  /**
   * 1. Instant Page Preloading on Hover / Touch
   * Pre-fetches internal links when user expresses intent, making next page load instant (< 300ms).
   */
  const preloadedUrls = new Set();

  function prefetchUrl(url) {
    if (!url || isDataSaver || preloadedUrls.has(url)) return;

    try {
      const parsed = new URL(url, window.location.href);
      // Only prefetch same-origin URLs, excluding cart/checkout/admin
      if (parsed.origin !== window.location.origin) return;
      if (/(\/cart|\/checkout|\/account|\/admin|\.json|\.js|\.css)/i.test(parsed.pathname)) return;

      preloadedUrls.add(url);

      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    } catch (e) {
      // Ignore URL parsing errors
    }
  }

  // Pre-fetch on touchstart (mobile) or mouseenter (desktop)
  let hoverTimer = null;

  function onLinkIntent(event) {
    const anchor = event.target.closest('a');
    if (!anchor || !anchor.href) return;

    if (event.type === 'touchstart') {
      prefetchUrl(anchor.href);
    } else if (event.type === 'mouseenter') {
      hoverTimer = setTimeout(() => {
        prefetchUrl(anchor.href);
      }, 65);
    }
  }

  function onLinkOut() {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      hoverTimer = null;
    }
  }

  document.addEventListener('touchstart', onLinkIntent, { passive: true });
  document.addEventListener('mouseenter', onLinkIntent, { passive: true, capture: true });
  document.addEventListener('mouseleave', onLinkOut, { passive: true, capture: true });

  /**
   * 2. Optimize Below-The-Fold Images & Lazyloading
   * Uses IntersectionObserver to trigger images slightly before they enter the viewport
   */
  if ('IntersectionObserver' in window) {
    const imgObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src && !img.classList.contains('lazyloaded')) {
            img.src = img.dataset.src;
            img.classList.add('lazyloaded');
          }
          observer.unobserve(img);
        }
      });
    }, { rootMargin: '300px 0px' });

    // Observe deferred images once DOM is ready
    const observeLazyImages = () => {
      document.querySelectorAll('img[data-src]:not(.lazyloaded)').forEach(img => {
        imgObserver.observe(img);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeLazyImages);
    } else {
      observeLazyImages();
    }
  }

  /**
   * 3. Defer Heavy Non-Critical Work Until Browser is Idle
   */
  const scheduleIdleTask = window.requestIdleCallback || function(cb) { setTimeout(cb, 1500); };

  scheduleIdleTask(() => {
    // Release memory and cache pre-connections
    if ('connection' in navigator && navigator.connection.saveData === false) {
      const preconnectDomains = [
        'https://cdn.shopify.com',
        'https://fonts.shopifycdn.com'
      ];
      preconnectDomains.forEach(domain => {
        const link = document.createElement('link');
        link.rel = 'dns-prefetch';
        link.href = domain;
        document.head.appendChild(link);
      });
    }
  });

})();
